/**
 * PaymentStep — the card step of registration (E23.9).
 *
 * App.jsx selects this view ahead of the auth gate whenever the stored token is a
 * registration session (useAuth `registrationSession`), so a customer whose tenant has
 * no card on file never enters the Console shell. It is a stage of registration, not a
 * Console tab: it renders no tab bar, no sidebar and no link into a Console surface
 * (FR4). Its ways out are completing it and signing out (FR5).
 *
 * What the step DOES lives in hooks/useCardStep.js, which reports a `phase` and a
 * `notice` code; this file owns only the words for them. Each sentence describes the
 * state the live standing reported, never what the landing URL implies (FR3), and none
 * mentions a charge: this flow makes none, and E21 shipped "no charge was made" as a
 * defect. The one place charging is discussed is S3's consent, rendered verbatim as a
 * single text node so that what is on screen is exactly the string
 * paymentConsent.test.js pins (D3).
 *
 * The copy does not assume a fresh registration: an account that predates E23 and has
 * no card reaches this step at its next sign-in.
 *
 * Chrome matches LoginScreen and ForgotPassword, the screens either side of this one.
 */

import { useState } from "react";
import useCardStep from "../hooks/useCardStep";
import { CONSENT_TEXT } from "../engine/paymentConsent";

// One sentence per notice useCardStep reports. "No card on file" is said only where the
// hook has read the live standing as pending; `error` covers an unknown standing, so it
// says nothing about the card.
const NOTICE_COPY = {
  cancelled:
    "Card setup was cancelled at our payment provider, so there is no card on file yet. You can start again below.",
  abandoned: "Your last card setup was not finished, so there is no card on file yet. You can start again below.",
  not_completed: "That card setup could not be completed, so there is no card on file yet. Start again below.",
  returned_unconfirmed:
    "You are back from our payment provider, and there is no card on file yet. Start again below to add your card.",
  consent_outdated:
    "The payment card authorisation has changed since this page loaded. Refresh the page to read the current version.",
  // A retry would meet the same refusal, so this says what to change, and ends on another browser,
  // which works whatever the cause. It never mentions the card: what is kept is a session id, and
  // "card" here reads as card details stored in the browser.
  storage_refused:
    "The payment page was not opened: this browser did not let the site keep a short setup reference. Check that this site is allowed to store data, then try again. If it still does not open, try another browser.",
  // Says nothing about what was saved: at completion the provider already holds the card, which
  // is why the retry can finish the same attempt without the customer entering it again.
  unavailable: "Card setup is unavailable right now. Try again in a moment.",
  error: "Something went wrong. Try again in a moment.",
};

// The two notices a retry can resolve: `retry` re-reads the standing and completes a kept
// attempt. Every other notice is answered by starting again.
const RETRY_NOTICES = new Set(["unavailable", "error"]);

export default function PaymentStep({ onComplete, onCardOnFile, onLogout }) {
  const { phase, notice, start, retry, logout } = useCardStep({ onComplete, onCardOnFile, onLogout });
  const [accepted, setAccepted] = useState(false);
  const redirecting = phase === "redirecting";
  // No form while the hook is deciding or completing, so a new attempt cannot start over one
  // that is finishing.
  const settling = phase === "checking" || phase === "completing";
  const problem = RETRY_NOTICES.has(notice);

  return (
    <div className="min-h-screen bg-canvas flex items-center justify-center px-4 py-8">
      <div data-testid="payment-step" className="w-full max-w-lg">
        <div className="bg-ink rounded-none px-7 pt-7 pb-6">
          <div className="text-white font-semibold text-2xl tracking-[0.22em]">OMEGA AI HPC</div>
          <div className="text-ink-muted text-sm mt-1">Add a payment card</div>
        </div>

        <div className="bg-surface border border-line border-t-0 rounded-none px-7 py-6">
          {settling ? (
            <div data-testid="payment-step-status" role="status" className="text-sm text-fg2 mb-4">
              {phase === "completing" ? "Confirming your card..." : "Checking your account..."}
            </div>
          ) : (
            <>
              {notice && (
                <div
                  data-testid="payment-step-notice"
                  role="status"
                  className={`text-sm rounded-none px-3 py-2 mb-4 leading-relaxed border ${
                    problem ? "text-danger bg-danger-tint border-danger/20" : "text-fg2 bg-surface2 border-line"
                  }`}
                >
                  <div>{NOTICE_COPY[notice]}</div>
                  {problem && (
                    <button
                      type="button"
                      onClick={retry}
                      data-testid="payment-step-retry"
                      className="mt-2 text-sm text-accent cursor-pointer hover:text-accent-strong bg-transparent border-0 p-0"
                    >
                      Try again
                    </button>
                  )}
                </div>
              )}

              <p className="text-sm text-fg2 mb-4 leading-relaxed">
                Your account needs a card on file before you can use the Console. You will be taken to
                our payment provider to enter your card, then brought back here.
              </p>

              <section aria-labelledby="payment-consent-title" className="mb-4">
                <h2 id="payment-consent-title" className="text-xs text-fg2 uppercase tracking-wide mb-1">
                  Payment card authorisation
                </h2>
                <div
                  data-testid="payment-step-consent"
                  className="text-sm text-fg2 leading-relaxed whitespace-pre-line bg-surface2 border border-line rounded-none px-3 py-2"
                >
                  {CONSENT_TEXT}
                </div>
              </section>

              <label className="flex items-start gap-2 text-sm text-fg mb-4 cursor-pointer">
                <input
                  type="checkbox"
                  checked={accepted}
                  onChange={(ev) => setAccepted(ev.target.checked)}
                  disabled={redirecting}
                  data-testid="payment-step-accept"
                  className="mt-1 accent-accent-fill"
                />
                <span>I have read and accept this authorisation</span>
              </label>

              <button
                type="button"
                onClick={start}
                disabled={!accepted || redirecting}
                aria-busy={redirecting}
                data-testid="payment-step-continue"
                className="w-full py-2.5 rounded-none bg-accent-fill text-white text-sm font-medium cursor-pointer hover:bg-accent-strong transition-colors duration-150 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {redirecting ? "Opening the payment page..." : "Add card"}
              </button>
            </>
          )}

          <button
            type="button"
            onClick={logout}
            data-testid="payment-step-logout"
            className="w-full py-2 mt-2 text-sm text-accent cursor-pointer hover:text-accent-strong bg-transparent border-0"
          >
            Logout
          </button>
        </div>
      </div>
    </div>
  );
}
